import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle, Building2, User, Mail, Phone, Users, MessageSquare } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface EmployerLeadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: string;
}

const EmployerLeadModal = ({ open, onOpenChange, source = "employers_page" }: EmployerLeadModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    companyName: "",
    contactName: "",
    email: "",
    phone: "",
    employeeCount: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save lead to database
      const { error } = await supabase.from("consultation_leads").insert({
        name: `${formData.contactName} (${formData.companyName})`,
        email: formData.email,
        phone: formData.phone,
        message: `Employee Count: ${formData.employeeCount}\n\nMessage: ${formData.message}\n\nSource: ${source}`,
        status: "new",
      });

      if (error) throw error;

      // Send notifications (Roam + GHL sync)
      try {
        await supabase.functions.invoke("send-employer-lead-notification", {
          body: {
            companyName: formData.companyName,
            contactName: formData.contactName,
            email: formData.email,
            phone: formData.phone,
            employeeCount: formData.employeeCount,
            message: formData.message,
            source,
          },
        });
      } catch (notifyError) {
        // Don't fail the submission if notifications fail
        console.error("Notification error (non-blocking):", notifyError);
      }

      setIsSubmitted(true);
      toast.success("Thank you! A True Consultant will be in touch soon.");
    } catch (error) {
      console.error("Error submitting employer lead:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset form after modal closes
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({
        companyName: "",
        contactName: "",
        email: "",
        phone: "",
        employeeCount: "",
        message: "",
      });
    }, 300);
  };

  if (isSubmitted) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center text-center py-8">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <DialogTitle className="text-2xl font-bold text-gray-900 mb-2">
              Thank You!
            </DialogTitle>
            <DialogDescription className="text-gray-600 mb-6">
              A True Wellness Consultant will be in touch within 24 hours to discuss how we can help transform your team's health.
            </DialogDescription>
            <Button onClick={handleClose} className="bg-[#0D1B2A] hover:bg-[#0D1B2A]/90">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-[#0D1B2A]" />
            Get in Touch
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Fill out the form below and a True Wellness Consultant will contact you within 24 hours.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="companyName" className="flex items-center gap-1">
                <Building2 className="w-4 h-4 text-gray-400" />
                Company Name *
              </Label>
              <Input
                id="companyName"
                required
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                placeholder="Acme Corp"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactName" className="flex items-center gap-1">
                <User className="w-4 h-4 text-gray-400" />
                Your Name *
              </Label>
              <Input
                id="contactName"
                required
                value={formData.contactName}
                onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                placeholder="Jane Smith"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-1">
                <Mail className="w-4 h-4 text-gray-400" />
                Work Email *
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="jane@company.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-1">
                <Phone className="w-4 h-4 text-gray-400" />
                Phone Number
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(555) 123-4567"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="employeeCount" className="flex items-center gap-1">
              <Users className="w-4 h-4 text-gray-400" />
              Number of Employees *
            </Label>
            <Select
              value={formData.employeeCount}
              onValueChange={(value) => setFormData({ ...formData, employeeCount: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select employee count" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10-50">10-50 employees</SelectItem>
                <SelectItem value="51-200">51-200 employees</SelectItem>
                <SelectItem value="201-500">201-500 employees</SelectItem>
                <SelectItem value="501-1000">501-1,000 employees</SelectItem>
                <SelectItem value="1001-5000">1,001-5,000 employees</SelectItem>
                <SelectItem value="5001-10000">5,001-10,000 employees</SelectItem>
                <SelectItem value="10001-50000">10,001-50,000 employees</SelectItem>
                <SelectItem value="50001+">50,001+ employees</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message" className="flex items-center gap-1">
              <MessageSquare className="w-4 h-4 text-gray-400" />
              How can we help?
            </Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Tell us about your wellness goals, timeline, or any questions you have..."
              rows={3}
            />
          </div>

          <Button 
            type="submit" 
            className="w-full bg-[#ffdb4d] text-[#0D1B2A] hover:bg-[#ffdb4d]/90 font-semibold py-6 text-lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Request Consultation"}
          </Button>

          <p className="text-xs text-center text-gray-500">
            By submitting, you agree to our privacy policy. We'll never share your information.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EmployerLeadModal;
